import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { HolderOutlined, PlusOutlined } from '@ant-design/icons';
import Draggable, { DraggableData, DraggableEvent } from 'react-draggable';
import { Resizable, ResizeCallbackData } from 'react-resizable';
import 'react-resizable/css/styles.css';
import { useSelector, useDispatch } from 'react-redux';
import { useResizeDetector } from 'react-resize-detector';
import { useDebouncedCallback } from 'use-debounce';
import { DataType, SupportedTypes } from '@kemu-io/hs-types';
import globalVariablesManager, { GlobalVariable, parsePath, buildFullPath } from '@kemu-io/kemu-core/common/managers/globalVariables.js';
import styles from './GlobalVariablesPanel.module.css';
import { selectLogicMapperSettings, setLogicMapperSettingValue } from '@src/features/interface/interfaceSlice';
import { selectCurrentRecipePoolId, selectOpeningRecipe } from '@src/features/Workspace/workspaceSlice';
import { useTranslation } from '@hooks/index';
import { useMinimizedPanels } from '@components/MinimizedPanelsContainer/MinimizedPanelsContext';
import { Button, Tooltip } from 'antd';
import usePanelMinimize from '@src/hooks/usePanelMinimize';
import PanelMinimizeButton from '@components/PanelMinimizeButton/PanelMinimizeButton';
import useGlobalNotification from '@src/common/hooks/useGlobalNotification';
import AddVariableModal from './AddVariableModal/AddVariableModal';
import ConfigurationModal from './ConfigurationModal/ConfigurationModal';
import ImportExportMenu from './ImportExportMenu';
import { ExportConfirmModal, ImportConfirmModal } from './ImportExportModals';
import {
  exportGlobalVariables,
  copyToClipboard,
  downloadFile,
  readClipboard,
  validateGlobalVariablesJson,
  readFileAsText,
  generateExportFilename,
} from './exportImportUtils';
import { deserializeGlobalVariables } from '@src/app/recipe/utils';
import NumberControl from './controls/NumberControl';
import SliderControl from './controls/SliderControl';
import TextControl from './controls/TextControl';
import MultilineTextControl from './controls/MultilineTextControl';
import DropdownControl from './controls/DropdownControl';
import ImageControl from './controls/ImageControl';
import BinaryFileControl from './controls/BinaryFileControl';
import CheckboxControl from './controls/CheckboxControl';
import MultiSelectControl from './controls/MultiSelectControl';
import AnythingControl from './controls/AnythingControl';
import ButtonControl from './controls/ButtonControl';
import SortableVariableRow from './SortableVariableRow/SortableVariableRow';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragEndEvent, DragStartEvent, DragMoveEvent, DragOverEvent, DragOverlay, CollisionDetection, MeasuringStrategy, UniqueIdentifier, DropAnimation, defaultDropAnimation } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { flattenTree, getProjection, removeChildrenOf, getChildCount, FlattenedItem } from './treeUtils';

type Props = {
  containerEl: HTMLDivElement | null;
}

type VariableTreeNode = {
  variable: GlobalVariable;
  children: VariableTreeNode[];
  depth: number;
}

const minPanelSize = { width: 300, height: 150 };

// Global variable for name column width percentage
const NAME_COLUMN_WIDTH_PERCENTAGE = 30; // Default 30% of total width

// Indentation width for tree structure (matches padding in SortableVariableRow)
const INDENTATION_WIDTH = 20;

// Drop animation configuration
const dropAnimationConfig: DropAnimation = {
  keyframes({ transform }) {
    return [
      { opacity: 1, transform: CSS.Transform.toString(transform.initial) },
      {
        opacity: 0,
        transform: CSS.Transform.toString({
          ...transform.final,
          x: transform.final.x + 5,
          y: transform.final.y + 5,
        }),
      },
    ];
  },
  easing: 'ease-out',
  sideEffects({ active }) {
    active.node.animate([{ opacity: 0 }, { opacity: 1 }], {
      duration: defaultDropAnimation.duration,
      easing: defaultDropAnimation.easing,
    });
  },
};


const GlobalVariablesPanel = (props: Props) => {
  const { containerEl } = props;
  const userSettings = useSelector(selectLogicMapperSettings);
  const defaultPanelPosition = userSettings.globalVariablesPanelPosition;
  const defaultPanelSize = userSettings.globalVariablesPanelSize || minPanelSize;
  const [panelPosition, setPanelPosition] = useState(defaultPanelPosition);
  const [panelSize, setPanelSize] = useState<{ width: number, height: number }>(defaultPanelSize);
  const [variableTree, setVariableTree] = useState<VariableTreeNode[]>([]);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [editingEntry, setEditingEntry] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState<string>('');
  const [dispatchingVariableChange, setDispatchingVariableChange] = useState<Record<string, boolean>>({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [configuringVariable, setConfiguringVariable] = useState<string | null>(null);
  const [showExportConfirm, setShowExportConfirm] = useState(false);
  const [exportAction, setExportAction] = useState<'download' | 'copy' | null>(null);
  const [exportGroupName, setExportGroupName] = useState<string | null>(null);
  const [showImportConfirm, setShowImportConfirm] = useState(false);
  const [pendingImportData, setPendingImportData] = useState<GlobalVariable[] | null>(null);
  const [importGroupName, setImportGroupName] = useState<string | null>(null);
  const [canPaste, setCanPaste] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [overId, setOverId] = useState<UniqueIdentifier | null>(null);
  const [offsetLeft, setOffsetLeft] = useState(0);
  const [offsetTop, setOffsetTop] = useState(0);
  const [currentPosition, setCurrentPosition] = useState<{
    parentId: UniqueIdentifier | null;
    overId: UniqueIdentifier;
  } | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor),
  );

  /**
   * Custom collision detection that respects group drop rules
   * Groups can only be dropped on other groups, not on regular variables
   * Note: We allow all collisions for visual feedback, but validate in handleDragEnd
   */
  const customCollisionDetection: CollisionDetection = useCallback((args) => {
    // Return all collisions to allow visual feedback (indicator)
    // Actual drop validation happens in handleDragEnd
    return closestCenter(args);
  }, []);

  const dispatch = useDispatch();
  const t = useTranslation('GlobalVariablesPanel');
  const { showNotification } = useGlobalNotification();
  const { registerExpandHandler, unregisterPanel } = useMinimizedPanels();
  const recipePoolId = useSelector(selectCurrentRecipePoolId);
  const openingRecipe = useSelector(selectOpeningRecipe);
  const { ref, width: screenWidth, height: screenHeight } = useResizeDetector();

  const canShowPanel = recipePoolId && !openingRecipe;

  const { isMinimized, handleMinimize, handleExpand } = usePanelMinimize({
    panelId: 'globalVariables',
    currentPosition: panelPosition,
    currentSize: panelSize,
    setPanelPosition,
    setPanelSize,
  });

  /**
   * Handles input change while editing
   * @param value - The new value
   */
  const handleInputChange = (value: string) => {
    setEditingValue(value);
  };

  /**
   * Handles saving the edited name
   * @param entry - The variable entry being edited
   */
  const handleSaveEdit = (entry: GlobalVariable) => {
    const newName = editingValue.trim();
    if (newName && newName !== entry.name) {
      // Validate that name doesn't contain dashes or dots
      if (newName.includes('-')) {
        showNotification({
          type: 'error',
          message: t('AddVariableModal.NameInvalid'),
          title: t.withBaseKey('CommonWords')('Error'),
        });
        handleCancelEdit();
        return;
      }
      if (newName.includes('.')) {
        showNotification({
          type: 'error',
          message: 'Variable and group names cannot contain dots',
          title: t.withBaseKey('CommonWords')('Error'),
        });
        handleCancelEdit();
        return;
      }
      // Extract display name from current entry name (in case it's a full path)
      const { name: currentDisplayName } = parsePath(entry.name);
      if (newName !== currentDisplayName) {
        globalVariablesManager.renameVariable({ oldName: entry.name, newName });
      }
    }
    setEditingEntry(null);
    setEditingValue('');
  };

  /**
   * Handles canceling the edit
   */
  const handleCancelEdit = () => {
    setEditingEntry(null);
    setEditingValue('');
  };

  /**
   * Handles key press events in the input
   * @param event - The keyboard event
   * @param entry - The variable entry being edited
   */
  const handleKeyPress = (event: React.KeyboardEvent, entry: GlobalVariable) => {
    if (event.key === 'Enter') {
      handleSaveEdit(entry);
    } else if (event.key === 'Escape') {
      handleCancelEdit();
    }
  };

  /**
   * Handles double-clicking on a variable name to start editing
   * @param entry - The variable entry being edited
   */
  const handleNameDoubleClick = (entry: GlobalVariable) => {
    setEditingEntry(entry.name);
    // Extract display name from full path for editing
    const { name: displayName } = parsePath(entry.name);
    setEditingValue(displayName);
  };

  /**
   * Handles clearing a variable's value
   * @param entry - The variable entry to clear
   */
  const handleClearValue = (entry: GlobalVariable) => {
    // Parse the full path to extract display name and group
    const { group, name: displayName } = parsePath(entry.name);
    globalVariablesManager.setGlobalVariable({
      name: displayName,
      value: null,
      lastDataType: entry.lastDataType,
      controlType: entry.controlType,
      controlSettings: entry.controlSettings,
      group: group,
    });

    renderVariableControls();
  };

  /**
   * Handles deleting a variable
   * @param entry - The variable entry to delete
   */
  const handleDeleteVariable = (entry: GlobalVariable) => {
    globalVariablesManager.deleteGlobalVariable({ name: entry.name, notify: true });
  };

  /**
   * Handles copying value based on control type
   * @param entry - The variable entry to copy
   */
  const handleCopy = (entry: GlobalVariable) => {
    if (entry.controlType === 'image' || (entry.controlType === 'anything' && entry.lastDataType === DataType.ImageData)) {
      handleCopyImage(entry);
    } else if (entry.controlType === 'anything') {
      handleCopyAnything(entry);
    }
  };

  /**
   * Handles opening the configuration modal
   * @param entry - The variable entry to configure
   */
  const handleConfigureVariable = (entry: GlobalVariable) => {
    setConfiguringVariable(entry.name);
  };

  const handlePushLocalValue = useCallback(async (entry: GlobalVariable) => {
    try {
      setDispatchingVariableChange((current) => ({ ...current, [entry.name]: true }));
      // Parse the full path to extract display name and group
      const { group, name: displayName } = parsePath(entry.name);
      await globalVariablesManager.setGlobalVariable({
        name: displayName,
        value: entry.lastValue,
        lastDataType: entry.lastDataType,
        controlType: entry.controlType,
        controlSettings: entry.controlSettings,
        group: group,
        forceValueChangeNotification: true,
      });
    } catch (error) {
      console.error('Error pushing local value:', error);
    } finally {
      setDispatchingVariableChange((current) => ({ ...current, [entry.name]: false }));
    }
  }, []);

  /**
   * Copies an image to clipboard as PNG
   * @param entry - The variable entry containing ImageData
   */
  const handleCopyImage = useCallback(async (entry: GlobalVariable) => {
    try {
      const imageData = entry.lastValue as ImageData | null;
      if (!imageData) {
        return;
      }

      const canvas = document.createElement('canvas');
      canvas.width = imageData.width;
      canvas.height = imageData.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return;
      }

      ctx.putImageData(imageData, 0, 0);
      canvas.toBlob(async (blob) => {
        if (blob) {
          await navigator.clipboard.write([
            new ClipboardItem({ 'image/png': blob })
          ]);
          showNotification({
            type: 'success',
            message: t('ImageCopied'),
            title: t.withBaseKey('CommonWords')('Success'),
          });
        }
      }, 'image/png');
    } catch (error) {
      console.error('Error copying image:', error);
    }
  }, [showNotification, t]);

  /**
   * Copies anything value to clipboard
   * @param entry - The variable entry
   */
  const handleCopyAnything = useCallback(async (entry: GlobalVariable) => {
    try {
      const value = entry.lastValue;
      if (value === null) {
        return;
      }

      let textToCopy: string;

      if (entry.lastDataType === DataType.String || entry.lastDataType === DataType.Number || entry.lastDataType === DataType.Boolean) {
        textToCopy = String(value);
      } else if (entry.lastDataType === DataType.Array || entry.lastDataType === DataType.JsonObj) {
        textToCopy = JSON.stringify(value, null, 2);
      } else {
        textToCopy = String(value);
      }

      await navigator.clipboard.writeText(textToCopy);
      showNotification({
        type: 'success',
        message: t('ValueCopied'),
        title: t.withBaseKey('CommonWords')('Success'),
      });
    } catch (error) {
      console.error('Error copying value:', error);
    }
  }, [showNotification, t]);

  /**
   * Downloads a file or image
   * @param entry - The variable entry containing BinaryFile or ImageData
   */
  const handleDownloadBinaryFile = useCallback((entry: GlobalVariable) => {
    try {
      // Check if it's an image
      if (entry.controlType === 'image' || (entry.controlType === 'anything' && entry.lastDataType === DataType.ImageData)) {
        const imageData = entry.lastValue as ImageData | null;
        if (!imageData) {
          return;
        }

        const canvas = document.createElement('canvas');
        canvas.width = imageData.width;
        canvas.height = imageData.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          return;
        }

        ctx.putImageData(imageData, 0, 0);
        canvas.toBlob((blob) => {
          if (blob) {
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${entry.name}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            showNotification({
              type: 'success',
              message: t('ImageDownloaded'),
              title: t.withBaseKey('CommonWords')('Success'),
            });
          }
        }, 'image/png');
        return;
      }

      // Handle binary file
      const binaryFile = entry.lastValue as any;
      if (!binaryFile || !binaryFile.data) {
        return;
      }

      const blob = new Blob([binaryFile.data], { type: binaryFile.format || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = binaryFile.fileName || `${entry.name}.bin`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      showNotification({
        type: 'success',
        message: t('FileDownloaded'),
        title: t.withBaseKey('CommonWords')('Success'),
      });
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  }, [showNotification, t]);

  /**
   * Builds a hierarchical tree structure from variables
   */
  const buildVariableTree = useCallback((entries: GlobalVariable[]): VariableTreeNode[] => {
    // Separate groups and regular variables
    const groups = entries.filter(v => globalVariablesManager.isGroupVariable(v));
    const regularVars = entries.filter(v => !globalVariablesManager.isGroupVariable(v));

    // Sort groups by order, then by name
    groups.sort((a, b) => {
      const aOrder = a.order ?? Infinity;
      const bOrder = b.order ?? Infinity;
      if (aOrder !== bOrder) {
        return aOrder - bOrder;
      }
      return a.name.localeCompare(b.name);
    });

    // Sort regular variables by order, then by name
    regularVars.sort((a, b) => {
      const aOrder = a.order ?? Infinity;
      const bOrder = b.order ?? Infinity;
      if (aOrder !== bOrder) {
        return aOrder - bOrder;
      }
      return a.name.localeCompare(b.name);
    });

    /**
     * Recursively builds tree structure for a group
     */
    const buildGroupTree = (groupVar: GlobalVariable, depth: number): VariableTreeNode => {
      const children: VariableTreeNode[] = [];
      
      // Find variables that belong to this group
      const directChildren = regularVars.filter(v => v.group === groupVar.name);
      directChildren.sort((a, b) => {
        const aOrder = a.order ?? Infinity;
        const bOrder = b.order ?? Infinity;
        if (aOrder !== bOrder) {
          return aOrder - bOrder;
        }
        return a.name.localeCompare(b.name);
      });

      // Find nested groups that belong to this group
      const nestedGroups = groups.filter(g => g.group === groupVar.name);
      nestedGroups.sort((a, b) => {
        const aOrder = a.order ?? Infinity;
        const bOrder = b.order ?? Infinity;
        if (aOrder !== bOrder) {
          return aOrder - bOrder;
        }
        return a.name.localeCompare(b.name);
      });

      // Add nested groups first
      for (const nestedGroup of nestedGroups) {
        children.push(buildGroupTree(nestedGroup, depth + 1));
      }

      // Add regular variables
      for (const child of directChildren) {
        children.push({
          variable: child,
          children: [],
          depth: depth + 1,
        });
      }

      return {
        variable: groupVar,
        children,
        depth,
      };
    };

    const tree: VariableTreeNode[] = [];

    // Combine top-level groups and ungrouped variables, then sort by order
    const topLevelGroups = groups.filter(g => !g.group || g.group === null);
    const ungroupedVars = regularVars.filter(v => !v.group || v.group === null);
    
    // Create a combined list of all top-level items
    const topLevelItems: Array<{ variable: GlobalVariable; isGroup: boolean }> = [
      ...topLevelGroups.map(g => ({ variable: g, isGroup: true })),
      ...ungroupedVars.map(v => ({ variable: v, isGroup: false })),
    ];

    // Sort all top-level items together by order, then by name
    topLevelItems.sort((a, b) => {
      const aOrder = a.variable.order ?? Infinity;
      const bOrder = b.variable.order ?? Infinity;
      if (aOrder !== bOrder) {
        return aOrder - bOrder;
      }
      return a.variable.name.localeCompare(b.variable.name);
    });

    // Build tree from sorted items
    for (const item of topLevelItems) {
      if (item.isGroup) {
        tree.push(buildGroupTree(item.variable, 0));
      } else {
        tree.push({
          variable: item.variable,
          children: [],
          depth: 0,
        });
      }
    }

    return tree;
  }, []);

  /**
   * Flattens the tree structure for drag and drop using utility function
   */
  const flattenedItems = useMemo(() => {
    const flattenedTree = flattenTree(variableTree, expandedGroups);
    const collapsedItems = flattenedTree.reduce<string[]>(
      (acc, { children, collapsed, id }) =>
        collapsed && children.length ? [...acc, id] : acc,
      []
    );

    return removeChildrenOf(
      flattenedTree,
      activeId != null ? [activeId, ...collapsedItems] : collapsedItems
    );
  }, [activeId, variableTree, expandedGroups]);

  /**
   * Calculates projection for drag position
   */
  const projected =
    activeId && overId
      ? getProjection(
          flattenedItems,
          activeId,
          overId,
          offsetLeft,
          INDENTATION_WIDTH
        )
      : null;

  /**
   * Gets the active item being dragged
   */
  const activeItem = activeId
    ? flattenedItems.find(({ id }) => id === activeId)
    : null;

  /**
   * Updates the variable entries from the global variables manager
   */
  const renderVariableControls = useCallback(() => {
    const entries = globalVariablesManager.getAllVariables();
    const tree = buildVariableTree(entries);
    setVariableTree(tree);
  }, [buildVariableTree]);

  /**
   * Toggles the expanded state of a group
   */
  const toggleGroup = useCallback((groupName: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupName)) {
        next.delete(groupName);
      } else {
        next.add(groupName);
      }
      return next;
    });
  }, []);


   /** Updates the variable value without triggering notifications */
   const handleLocalControlValueChange = useCallback((name: string, value: SupportedTypes, type: DataType) => {
    // Parse the full path to extract display name and group
    const { group, name: displayName } = parsePath(name);
    
    globalVariablesManager.setGlobalVariable({
      name: displayName,
      value: value,
      lastDataType: type,
      group: group,
      // forceValueChangeNotification: true,
      disableChangeNotifications: true,
      disableDefineNotifications: true,
    });

    renderVariableControls();
  }, [renderVariableControls]);


  /**
   * Handles drag start event
   */
  const handleDragStart = useCallback((event: DragStartEvent) => {
    const { active } = event;
    setActiveId(active.id);
    setOverId(active.id);

    const activeItem = flattenedItems.find(({ id }) => id === active.id);
    if (activeItem) {
      setCurrentPosition({
        parentId: activeItem.parentId,
        overId: active.id,
      });
    }

    document.body.style.setProperty('cursor', 'grabbing');
  }, [flattenedItems]);

  /**
   * Handles drag move event to track horizontal and vertical offset
   */
  const handleDragMove = useCallback((event: DragMoveEvent) => {
    setOffsetLeft(event.delta.x);
    setOffsetTop(event.delta.y);
  }, []);

  /**
   * Handles drag over event
   */
  const handleDragOver = useCallback((event: DragOverEvent) => {
    setOverId(event.over?.id ?? null);
  }, []);

  /**
   * Handles drag end event and updates variable order or group assignment
   */
  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;

    // Capture offsetLeft before resetting state
    const currentOffsetLeft = offsetLeft;
    
    resetDragState();

    if (!over || !projected) {
      renderVariableControls();
      return;
    }

    const draggedVariable = globalVariablesManager.getGlobalVariable(active.id as string);
    const targetVariable = globalVariablesManager.getGlobalVariable(over.id as string);

    if (!draggedVariable || !targetVariable) {
      renderVariableControls();
      return;
    }

    const isDraggedGroup = globalVariablesManager.isGroupVariable(draggedVariable);
    const isTargetGroup = globalVariablesManager.isGroupVariable(targetVariable);

    // Validate drop: groups can only be nested into other groups (not into regular variables)
    // But groups can be reordered relative to regular variables
    // Find the dragged item to check its current parent
    const draggedItem = flattenedItems.find(({ id }) => id === active.id);
    const draggedParentId = draggedItem?.parentId ?? null;

    // Use projection to determine if we should nest or reorder
    const { depth, parentId } = projected;
    
    // Find target item in flattened list to compare depths
    const targetItem = flattenedItems.find(({ id }) => id === over.id);
    const targetDepth = targetItem?.depth ?? 0;

    // Determine if we're nesting or reordering:
    // Nesting occurs when (matching the render logic for consistency):
    // 1. The projected parentId is set to the target group name AND we're moving into it (not already in it)
    // 2. OR the projected depth is greater than target depth (dragging right to nest)
    // 3. OR we're dragging right (positive offset) over a group - this matches the render indicator logic
    // If we're already in the target group and not dragging right/increasing depth, skip nesting (reorder instead)
    const isMovingIntoGroup = parentId === targetVariable.name && parentId !== draggedParentId;
    const isDraggingRight = currentOffsetLeft > INDENTATION_WIDTH / 2;
    const isDepthIncreasing = depth > targetDepth;
    const isAlreadyInTargetGroup = draggedParentId === targetVariable.name;
    const isNesting = isTargetGroup && 
                      draggedVariable.name !== targetVariable.name &&
                      (isMovingIntoGroup || 
                       isDepthIncreasing ||
                       (isDraggingRight && !isAlreadyInTargetGroup));

    // Prevent nesting groups into non-group variables
    if (isNesting && !isTargetGroup) {
      renderVariableControls();
      return;
    }

    // Nesting: assign to group
    if (isNesting && isTargetGroup && draggedVariable.name !== targetVariable.name) {
      setExpandedGroups(prev => {
        const next = new Set(prev);
        next.add(targetVariable.name);
        return next;
      });

      // Parse the dragged variable's name to get display name
      const { name: displayName } = parsePath(draggedVariable.name);
      
      // If moving a group, we need to move all its children too
      if (isDraggedGroup) {
        // Get all variables in the dragged group (including nested groups)
        const variablesInGroup = globalVariablesManager.getVariablesInGroup(draggedVariable.name);
        
        // Delete the old group variable first (this will also delete all its children)
        globalVariablesManager.deleteGlobalVariable({ name: draggedVariable.name, notify: false });
        
        // Recreate the group in the new location
        globalVariablesManager.setGlobalVariable({
          name: displayName,
          value: draggedVariable.lastValue,
          lastDataType: draggedVariable.lastDataType,
          controlType: draggedVariable.controlType,
          controlSettings: draggedVariable.controlSettings as any,
          group: targetVariable.name,
          order: draggedVariable.order,
          helpText: draggedVariable.helpText,
          disableChangeNotifications: false,
          disableDefineNotifications: false,
        } as any);
        
        // Get the new group path
        const newGroupPath = buildFullPath(targetVariable.name, displayName);
        
        // Recreate all children in the new location
        for (const childVar of variablesInGroup) {
          const { name: childDisplayName } = parsePath(childVar.name);
          // Calculate the new group path for this child
          // If the child was directly in the old group, it goes directly in the new group
          // If the child was in a nested group, we need to preserve the nested structure
          let newChildGroup: string | null;
          if (childVar.group === draggedVariable.name) {
            // Direct child - goes directly in new group
            newChildGroup = newGroupPath;
          } else if (childVar.group && childVar.group.startsWith(draggedVariable.name + '.')) {
            // Nested child - rebuild the path by replacing the old group prefix with the new one
            const nestedPath = childVar.group.substring(draggedVariable.name.length + 1);
            // nestedPath is already a group path (may contain dots), so concatenate directly
            newChildGroup = newGroupPath + '.' + nestedPath;
          } else {
            // Shouldn't happen, but fallback
            newChildGroup = childVar.group ?? null;
          }
          
          globalVariablesManager.setGlobalVariable({
            name: childDisplayName,
            value: childVar.lastValue,
            lastDataType: childVar.lastDataType,
            controlType: childVar.controlType,
            controlSettings: childVar.controlSettings as any,
            group: newChildGroup,
            order: childVar.order,
            helpText: childVar.helpText,
            disableChangeNotifications: false,
            disableDefineNotifications: false,
          } as any);
        }
      } else {
        // Moving a regular variable - delete the old one first
        globalVariablesManager.deleteGlobalVariable({ name: draggedVariable.name, notify: false });
        
        // Create the variable in the new location
        globalVariablesManager.setGlobalVariable({
          name: displayName,
          value: draggedVariable.lastValue,
          lastDataType: draggedVariable.lastDataType,
          controlType: draggedVariable.controlType,
          controlSettings: draggedVariable.controlSettings as any,
          group: targetVariable.name,
          order: draggedVariable.order,
          helpText: draggedVariable.helpText,
          disableChangeNotifications: false,
          disableDefineNotifications: false,
        } as any);
      }
      
      renderVariableControls();
      return;
    }

    // Reordering: update order properties
    const oldIndex = flattenedItems.findIndex(({ id }) => id === active.id);
    const overIndex = flattenedItems.findIndex(({ id }) => id === over.id);

    if (oldIndex !== -1 && overIndex !== -1 && oldIndex !== overIndex) {
      const clonedItems: FlattenedItem[] = JSON.parse(JSON.stringify(flattenedItems));
      const activeTreeItem = clonedItems[oldIndex];
      clonedItems[oldIndex] = { ...activeTreeItem, depth, parentId };

      const sortedItems = arrayMove(clonedItems, oldIndex, overIndex);

      // Update all affected items' order and group properties
      const allVariables = globalVariablesManager.getAllVariables();
      const variableMap = new Map(allVariables.map(v => [v.name, v]));

      // Rebuild order based on new positions
      let currentOrder = 0;
      for (const item of sortedItems) {
        const variable = variableMap.get(item.id);
        if (!variable) continue;

        const newGroup = item.parentId || null;
        const isGroup = globalVariablesManager.isGroupVariable(variable);

        // Parse variable name to get display name
        const { name: displayName } = parsePath(variable.name);
        
        // Update group assignment if changed
        if (variable.group !== newGroup) {
          // Group changed - need to move the variable (and its children if it's a group)
          if (isGroup) {
            // Moving a group - get all children first
            const variablesInGroup = globalVariablesManager.getVariablesInGroup(variable.name);
            
            // Delete the old group variable first (this will also delete all its children)
            globalVariablesManager.deleteGlobalVariable({ name: variable.name, notify: false });
            
            // Recreate the group in the new location
            globalVariablesManager.setGlobalVariable({
              name: displayName,
              value: variable.lastValue,
              lastDataType: variable.lastDataType,
              controlType: variable.controlType,
              controlSettings: variable.controlSettings as any,
              group: newGroup,
              order: currentOrder,
              helpText: variable.helpText,
              disableChangeNotifications: false,
              disableDefineNotifications: false,
            } as any);
            
            // Get the new group path
            const newGroupPath = buildFullPath(newGroup, displayName);
            
            // Recreate all children in the new location
            for (const childVar of variablesInGroup) {
              const { name: childDisplayName } = parsePath(childVar.name);
              // Calculate the new group path for this child
              let newChildGroup: string | null;
              if (childVar.group === variable.name) {
                // Direct child - goes directly in new group
                newChildGroup = newGroupPath;
              } else if (childVar.group && childVar.group.startsWith(variable.name + '.')) {
                // Nested child - rebuild the path
                const nestedPath = childVar.group.substring(variable.name.length + 1);
                newChildGroup = newGroupPath + '.' + nestedPath;
              } else {
                // Shouldn't happen, but fallback
                newChildGroup = childVar.group ?? null;
              }
              
              globalVariablesManager.setGlobalVariable({
                name: childDisplayName,
                value: childVar.lastValue,
                lastDataType: childVar.lastDataType,
                controlType: childVar.controlType,
                controlSettings: childVar.controlSettings as any,
                group: newChildGroup,
                order: childVar.order,
                helpText: childVar.helpText,
                disableChangeNotifications: false,
                disableDefineNotifications: false,
              } as any);
            }
          } else {
            // Moving a regular variable - delete old one first
            globalVariablesManager.deleteGlobalVariable({ name: variable.name, notify: false });
            
            // Create variable in new location
            globalVariablesManager.setGlobalVariable({
              name: displayName,
              value: variable.lastValue,
              lastDataType: variable.lastDataType,
              controlType: variable.controlType,
              controlSettings: variable.controlSettings as any,
              group: newGroup,
              order: currentOrder,
              helpText: variable.helpText,
              disableChangeNotifications: false,
              disableDefineNotifications: false,
            } as any);
          }
        } else {
          // Just update order (but still need to parse and rebuild path)
          globalVariablesManager.setGlobalVariable({
            name: displayName,
            value: variable.lastValue,
            lastDataType: variable.lastDataType,
            controlType: variable.controlType,
            controlSettings: variable.controlSettings as any,
            group: variable.group ?? null,
            order: currentOrder,
            helpText: variable.helpText,
            disableChangeNotifications: false,
            disableDefineNotifications: false,
          } as any);
        }

        currentOrder++;
      }
    }

    renderVariableControls();
  }, [flattenedItems, projected, offsetLeft, renderVariableControls]);

  /**
   * Handles drag cancel event
   */
  const handleDragCancel = useCallback(() => {
    resetDragState();
    renderVariableControls();
  }, [renderVariableControls]);

  /**
   * Resets drag state
   */
  const resetDragState = useCallback(() => {
    setOverId(null);
    setActiveId(null);
    setOffsetLeft(0);
    setOffsetTop(0);
    setCurrentPosition(null);
    document.body.style.setProperty('cursor', '');
  }, []);

  /**
   * Debounced handler to save size to Redux after user stops resizing
   * This prevents excessive Redux updates during resize
   */
  const debouncedSaveSize = useDebouncedCallback((size: { width: number; height: number }) => {
    dispatch(setLogicMapperSettingValue({
      key: 'globalVariablesPanelSize',
      value: size,
    }));
  }, 300);

  const handleResize = useCallback((event: React.SyntheticEvent, data: ResizeCallbackData) => {
    let newWidth = Math.max(minPanelSize.width, data.size.width);
    let newHeight = Math.max(minPanelSize.height, data.size.height);
    const rightEdge = panelPosition.x + newWidth + 10;
    const bottomEdge = panelPosition.y + newHeight + 10;

    if(screenWidth && rightEdge > screenWidth) {
      newWidth = screenWidth - panelPosition.x - 10;
    }

    if(screenHeight && bottomEdge > screenHeight) {
      newHeight = screenHeight - panelPosition.y - 10;
    }

    const finalSize = {
      width: newWidth,
      height: newHeight,
    };

    // Update local state immediately for responsive UI
    setPanelSize(finalSize);

    // Debounce Redux update to prevent excessive re-renders
    debouncedSaveSize(finalSize);
  }, [screenWidth, screenHeight, panelPosition, debouncedSaveSize]);

  const handleStopDragging = (_: DraggableEvent, data: DraggableData) => {
    setPanelPosition({
      x: data.x,
      y: data.y,
    });

    dispatch(setLogicMapperSettingValue({
      key: 'globalVariablesPanelPosition',
      value: {
        x: data.x,
        y: data.y,
      },
    }));
  };


  useEffect(() => {
    if (containerEl) {
      ref.current = containerEl;
    }
  }, [containerEl, ref]);

  // Prevents panel dimensions from exceeding viewport
  useEffect(() => {
    if (screenWidth && screenHeight) {
      setPanelSize((currentSize) => {
        // Check if the panel dimensions exceed viewport
        const newWidth = Math.max(minPanelSize.width, Math.min(currentSize.width, screenWidth));
        const newHeight = Math.max(minPanelSize.height, Math.min(currentSize.height, screenHeight));
        
        return {
          width: newWidth,
          height: newHeight,
        };
      });
    }
  }, [screenWidth, screenHeight]);

  // Prevents panel position from being outside the visible workspace
  useEffect(() => {
    if (screenWidth && screenHeight) {
      setPanelPosition((currentPos) => {
        let newX = currentPos.x;
        let newY = currentPos.y;

        // Check if panel + size exceeds viewport bounds
        if (screenWidth && (currentPos.x + panelSize.width + 10) > screenWidth) {
          // Try to move the panel to fit
          newX = Math.max(0, screenWidth - panelSize.width - 10);
          // If moving would place panel at negative coords, don't move
          if (newX < 0) {
            newX = currentPos.x;
          }
        }

        if (screenHeight && (currentPos.y + panelSize.height + 10) > screenHeight) {
          // Try to move the panel to fit
          newY = Math.max(0, screenHeight - panelSize.height - 10);
          // If moving would place panel at negative coords, don't move
          if (newY < 0) {
            newY = currentPos.y;
          }
        }

        // Also check minimum panel size with current position
        if (screenWidth && screenWidth < (newX + minPanelSize.width + 10)) {
          newX = screenWidth - minPanelSize.width - 10;
        }
        if (screenHeight && screenHeight < (newY + minPanelSize.height + 10)) {
          newY = screenHeight - minPanelSize.height - 10;
        }

        return {
          x: newX,
          y: newY,
        };
      });
    }
  }, [screenWidth, screenHeight, panelSize]);

  // Initialize variable entries and subscribe to changes
  useEffect(() => {
    renderVariableControls();

    const unsubscribe = globalVariablesManager.onAnyGlobalVariableChange(renderVariableControls);
    const unsubscribeDefined = globalVariablesManager.onGlobalVariableDefined(renderVariableControls);
    const unsubscribeRemoved = globalVariablesManager.onGlobalVariableRemoved(renderVariableControls);
    
    return () => {
      unsubscribe();
      unsubscribeDefined();
      unsubscribeRemoved();
    };
  }, [renderVariableControls]);

  // Register/unregister expand handler with context
  useEffect(() => {
    if (isMinimized) {
      registerExpandHandler('globalVariables', handleExpand, t('Title'));
    } else {
      unregisterPanel('globalVariables');
    }
    return () => {
      unregisterPanel('globalVariables');
    };
  }, [isMinimized, handleExpand, t, registerExpandHandler, unregisterPanel]);

  // Check clipboard for valid paste data
  const checkClipboard = useCallback(async () => {
    try {
      const clipboardText = await readClipboard();
      if (clipboardText) {
        const validation = validateGlobalVariablesJson(clipboardText);
        setCanPaste(validation.valid);
      } else {
        setCanPaste(false);
      }
    } catch (error) {
      setCanPaste(false);
    }
  }, []);

  /**
   * Handles dot menu open/close - checks clipboard when menu opens
   * @param open - Whether the menu is opening (true) or closing (false)
   */
  const handleMenuOpenChange = useCallback((open: boolean) => {
    if (open) {
      checkClipboard();
    }
  }, [checkClipboard]);

  // Sorted IDs for SortableContext
  const sortedIds = useMemo(
    () => flattenedItems.map(({ id }) => id),
    [flattenedItems]
  );

  /**
   * Handles button click for button control type - reuses handlePushLocalValue logic
   * @param entry - The variable entry
   */
  const handleButtonClick = useCallback(async (entry: GlobalVariable) => {
    // Create a modified entry with button value (true) and boolean type
    const buttonEntry: GlobalVariable = {
      ...entry,
      lastValue: true,
      lastDataType: DataType.Boolean,
    };
    await handlePushLocalValue(buttonEntry);
  }, [handlePushLocalValue]);

  /**
   * Handles export click - shows confirmation modal
   */
  const handleExportClick = useCallback(() => {
    setExportAction('download');
    setExportGroupName(null);
    setShowExportConfirm(true);
  }, []);

  /**
   * Handles toggling the save values in recipe setting
   */
  const handleSaveValuesToggle = useCallback(() => {
    dispatch(setLogicMapperSettingValue({
      key: 'saveGlobalVariableValues',
      value: !userSettings.saveGlobalVariableValues,
    }));
  }, [dispatch, userSettings.saveGlobalVariableValues]);

  /**
   * Handles export with includeValues option
   * @param includeValues - Whether to include variable values in export
   */
  const handleExport = useCallback((includeValues: boolean) => {
    try {
      let variablesToExport: GlobalVariable[];
      if (exportGroupName) {
        // Export group and its variables
        const groupVar = globalVariablesManager.getGlobalVariable(exportGroupName);
        const variablesInGroup = globalVariablesManager.getVariablesInGroup(exportGroupName);
        if (groupVar) {
          variablesToExport = [groupVar, ...variablesInGroup];
        } else {
          variablesToExport = variablesInGroup;
        }
      } else {
        variablesToExport = globalVariablesManager.getAllVariables();
      }
      const jsonString = exportGlobalVariables(variablesToExport, includeValues);
      const filename = generateExportFilename();
      downloadFile(jsonString, filename);
      showNotification({
        type: 'success',
        message: t('ExportSuccess'),
        title: t.withBaseKey('CommonWords')('Success'),
      });
    } catch (error) {
      console.error('Export failed:', error);
      showNotification({
        type: 'error',
        message: t('ExportError'),
        title: t.withBaseKey('CommonWords')('Error'),
      });
    }
    setShowExportConfirm(false);
    setExportAction(null);
    setExportGroupName(null);
  }, [showNotification, t, exportGroupName]);

  /**
   * Handles copy click - shows export confirmation, then copies to clipboard
   */
  const handleCopyClick = useCallback(() => {
    setExportAction('copy');
    setExportGroupName(null);
    setShowExportConfirm(true);
  }, []);

  /**
   * Handles copy after export confirmation
   * @param includeValues - Whether to include variable values
   */
  const handleCopyVarExport = useCallback(async (includeValues: boolean) => {
    try {
      let variablesToExport: GlobalVariable[];
      if (exportGroupName) {
        // Copy group and its variables
        const groupVar = globalVariablesManager.getGlobalVariable(exportGroupName);
        const variablesInGroup = globalVariablesManager.getVariablesInGroup(exportGroupName);
        if (groupVar) {
          variablesToExport = [groupVar, ...variablesInGroup];
        } else {
          variablesToExport = variablesInGroup;
        }
      } else {
        variablesToExport = globalVariablesManager.getAllVariables();
      }
      const jsonString = exportGlobalVariables(variablesToExport, includeValues);
      await copyToClipboard(jsonString);
      showNotification({
        type: 'success',
        message: t('CopySuccess'),
        title: t.withBaseKey('CommonWords')('Success'),
      });
    } catch (error) {
      console.error('Copy failed:', error);
      showNotification({
        type: 'error',
        message: t('CopyError'),
        title: t.withBaseKey('CommonWords')('Error'),
      });
    }
    setShowExportConfirm(false);
    setExportAction(null);
    setExportGroupName(null);
  }, [showNotification, t, exportGroupName]);

  /**
   * Handles paste click - validates clipboard and imports
   */
  const handlePasteClick = useCallback(async () => {
    try {
      const clipboardText = await readClipboard();
      if (!clipboardText) {
        showNotification({
          type: 'error',
          message: t('PasteError'),
          title: t.withBaseKey('CommonWords')('Error'),
        });
        return;
      }

      const validation = validateGlobalVariablesJson(clipboardText);
      if (!validation.valid || !validation.data) {
        showNotification({
          type: 'error',
          message: t('InvalidConfiguration'),
          title: t.withBaseKey('CommonWords')('Error'),
        });
        return;
      }

      const existingVariables = globalVariablesManager.getAllVariables();
      if (existingVariables.length > 0) {
        setPendingImportData(validation.data);
        setShowImportConfirm(true);
      } else {
        await handleImport('replace', validation.data);
      }
    } catch (error) {
      console.error('Paste failed:', error);
      showNotification({
        type: 'error',
        message: t('PasteError'),
        title: t.withBaseKey('CommonWords')('Error'),
      });
    }
  }, [showNotification, t]);

  /**
   * Handles import click - opens file input
   */
  const handleImportClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  /**
   * Handles import with mode (add or replace)
   * @param mode - 'add' to add to existing, 'replace' to replace all
   * @param variables - The variables to import
   */
  const handleImport = useCallback(async (mode: 'add' | 'replace', variables: GlobalVariable[]) => {
    try {
      // Check if any variables have values that need deserialization
      const hasValues = variables.some(v => v.lastValue !== null && v.lastValue !== undefined);
      
      let variablesToImport = variables;
      if (hasValues) {
        // Deserialize binary data from base64
        variablesToImport = await deserializeGlobalVariables(variables);
      }

      // Migrate variables: if name doesn't contain dots and group exists, rebuild path
      const migratedVariables = variablesToImport.map(variable => {
        // If name doesn't contain dots, it's either ungrouped or needs migration
        if (!variable.name.includes('.')) {
          if (variable.group) {
            // Rebuild path from group + name
            const fullPath = buildFullPath(variable.group, variable.name);
            return {
              ...variable,
              name: fullPath,
            };
          }
          // Ungrouped variable, keep name as-is
          return variable;
        }
        // Already migrated or invalid - validate display name doesn't contain dots
        const { name: displayName } = parsePath(variable.name);
        if (displayName.includes('.')) {
          throw new Error(`Invalid variable name: "${variable.name}" - display names cannot contain dots`);
        }
        return variable;
      });

      if (mode === 'replace') {
        globalVariablesManager.registerGlobalVariables(migratedVariables);
      } else {
        // Add mode: set each variable individually
        for (const variable of migratedVariables) {
          // Parse full path to get display name and group
          const { group, name: displayName } = parsePath(variable.name);
          await globalVariablesManager.setGlobalVariable({
            name: displayName,
            value: variable.lastValue,
            lastDataType: variable.lastDataType ?? null,
            controlType: variable.controlType,
            controlSettings: variable.controlSettings as any,
            helpText: variable.helpText,
            order: variable.order,
            group: group,
            disableChangeNotifications: true,
            disableDefineNotifications: true,
          });
        }
      }

      showNotification({
        type: 'success',
        message: t('ImportSuccess'),
        title: t.withBaseKey('CommonWords')('Success'),
      });

      renderVariableControls();
    } catch (error) {
      console.error('Import failed:', error);
      showNotification({
        type: 'error',
        message: t('ImportError'),
        title: t.withBaseKey('CommonWords')('Error'),
      });
    }

    setShowImportConfirm(false);
    setPendingImportData(null);
    setImportGroupName(null);
  }, [showNotification, t, renderVariableControls]);

  /**
   * Handles file selection for import
   */
  const handleFileSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      const fileContent = await readFileAsText(file);
      const validation = validateGlobalVariablesJson(fileContent);
      
      if (!validation.valid || !validation.data) {
        showNotification({
          type: 'error',
          message: t('InvalidConfiguration'),
          title: t.withBaseKey('CommonWords')('Error'),
        });
        return;
      }

      const existingVariables = globalVariablesManager.getAllVariables();
      if (existingVariables.length > 0) {
        setPendingImportData(validation.data);
        setImportGroupName(null);
        setShowImportConfirm(true);
      } else {
        await handleImport('replace', validation.data);
      }
    } catch (error) {
      console.error('Import failed:', error);
      showNotification({
        type: 'error',
        message: t('ImportError'),
        title: t.withBaseKey('CommonWords')('Error'),
      });
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [showNotification, t, handleImport]);

  

  /**
   * Handles export confirmation modal result
   */
  const handleExportConfirm = useCallback((includeValues: boolean) => {
    if (exportAction === 'download') {
      handleExport(includeValues);
    } else if (exportAction === 'copy') {
      handleCopyVarExport(includeValues);
    }
  }, [exportAction, handleExport, handleCopyVarExport]);

  

  /**
   * Handles group export - exports group variable and all variables in that group
   * @param groupName - The name of the group to export
   */
  const handleGroupExport = useCallback((groupName: string) => {
    setExportAction('download');
    setExportGroupName(groupName);
    setShowExportConfirm(true);
  }, []);

  /**
   * Handles group copy - copies group variable and all variables in that group
   * @param groupName - The name of the group to copy
   */
  const handleGroupCopy = useCallback((groupName: string) => {
    setExportAction('copy');
    setExportGroupName(groupName);
    setShowExportConfirm(true);
  }, []);

  /**
   * Handles group paste - imports variables into a specific group
   * @param groupName - The name of the group to import into
   */
  const handleGroupPaste = useCallback(async (groupName: string) => {
    try {
      const clipboardText = await readClipboard();
      if (!clipboardText) {
        showNotification({
          type: 'error',
          message: t('PasteError'),
          title: t.withBaseKey('CommonWords')('Error'),
        });
        return;
      }

      const validation = validateGlobalVariablesJson(clipboardText);
      if (!validation.valid || !validation.data) {
        showNotification({
          type: 'error',
          message: t('InvalidConfiguration'),
          title: t.withBaseKey('CommonWords')('Error'),
        });
        return;
      }

      // Ensure group exists
      const groupVar = globalVariablesManager.getGlobalVariable(groupName);
      if (!groupVar || !globalVariablesManager.isGroupVariable(groupVar)) {
        showNotification({
          type: 'error',
          message: t('GroupNotFound'),
          title: t.withBaseKey('CommonWords')('Error'),
        });
        return;
      }

      setPendingImportData(validation.data);
      setImportGroupName(groupName);
      setShowImportConfirm(true);
    } catch (error) {
      console.error('Group paste failed:', error);
      showNotification({
        type: 'error',
        message: t('PasteError'),
        title: t.withBaseKey('CommonWords')('Error'),
      });
    }
  }, [showNotification, t]);

  /**
   * Handles group import with mode - imports variables and assigns them to the group
   * @param groupName - The name of the group
   * @param mode - 'add' or 'replace'
   * @param variables - The variables to import
   */
  const handleGroupImportWithMode = useCallback(async (groupName: string, mode: 'add' | 'replace', variables: GlobalVariable[]) => {
    try {
      // Ensure group exists
      const groupVar = globalVariablesManager.getGlobalVariable(groupName);
      if (!groupVar || !globalVariablesManager.isGroupVariable(groupVar)) {
        showNotification({
          type: 'error',
          message: t('GroupNotFound'),
          title: t.withBaseKey('CommonWords')('Error'),
        });
        return;
      }

      // Check if any variables have values that need deserialization
      const hasValues = variables.some(v => v.lastValue !== null && v.lastValue !== undefined);
      
      let variablesToImport = variables;
      if (hasValues) {
        variablesToImport = await deserializeGlobalVariables(variables);
      }

      // Filter out root level groups
      const variablesWithoutRootLevelGroups = variablesToImport.filter(v => !(
        !v.group // without a group
        && v.controlType === 'group' // and is a group
      ));

      // Migrate and assign all variables to the group
      const variablesWithGroup = variablesWithoutRootLevelGroups.map(v => {
        // Migrate: if name doesn't contain dots, rebuild path
        let migratedName = v.name;
        if (!v.name.includes('.')) {
          // If it has a group in the import, use that for migration, otherwise use target group
          const sourceGroup = v.group || null;
          migratedName = buildFullPath(sourceGroup, v.name);
        }
        
        // Validate display name doesn't contain dots
        const { name: displayName } = parsePath(migratedName);
        if (displayName.includes('.')) {
          throw new Error(`Invalid variable name: "${migratedName}" - display names cannot contain dots`);
        }
        
        return {
          ...v,
          name: migratedName,
          group: groupName, // Assign to target group
        };
      });

      if (mode === 'replace') {
        // Remove existing variables in the group first
        const existingInGroup = globalVariablesManager.getVariablesInGroup(groupName);
        for (const existing of existingInGroup) {
          if (existing.controlType !== 'group') {
            globalVariablesManager.deleteGlobalVariable({ name: existing.name, notify: false });
          }
        }
      }

      // Add new variables to the group
      for (const variable of variablesWithGroup) {
        // Skip if it's the group variable itself
        if (variable.name === groupName) {
          continue;
        }

        // Parse full path to get display name
        const { name: displayName } = parsePath(variable.name);
        
        await globalVariablesManager.setGlobalVariable({
          name: displayName,
          value: variable.lastValue,
          lastDataType: variable.lastDataType ?? null,
          controlType: variable.controlType,
          controlSettings: variable.controlSettings as any,
          helpText: variable.helpText,
          order: variable.order,
          group: groupName,
          disableChangeNotifications: true,
          disableDefineNotifications: true,
        });
      }

      showNotification({
        type: 'success',
        message: t('ImportSuccess'),
        title: t.withBaseKey('CommonWords')('Success'),
      });

      renderVariableControls();
    } catch (error) {
      console.error('Group import failed:', error);
      showNotification({
        type: 'error',
        message: t('ImportError'),
        title: t.withBaseKey('CommonWords')('Error'),
      });
    }

    setShowImportConfirm(false);
    setPendingImportData(null);
  }, [showNotification, t, renderVariableControls]);

  /**
   * Handles import confirmation modal result
   */
  const handleImportConfirm = useCallback((mode: 'add' | 'replace') => {
    if (pendingImportData) {
      if (importGroupName) {
        handleGroupImportWithMode(importGroupName, mode, pendingImportData);
      } else {
        handleImport(mode, pendingImportData);
      }
    }
  }, [pendingImportData, importGroupName, handleImport, handleGroupImportWithMode]);

  /**
   * Handles group file import - processes file for group import
   * @param groupName - The name of the group
   * @param file - The file to import
   */
  const handleGroupFileImport = useCallback(async (groupName: string, file: File) => {
    try {
      const fileContent = await readFileAsText(file);
      const validation = validateGlobalVariablesJson(fileContent);
      
      if (!validation.valid || !validation.data) {
        showNotification({
          type: 'error',
          message: t('InvalidConfiguration'),
          title: t.withBaseKey('CommonWords')('Error'),
        });
        return;
      }

      // Ensure group exists
      const groupVar = globalVariablesManager.getGlobalVariable(groupName);
      if (!groupVar || !globalVariablesManager.isGroupVariable(groupVar)) {
        showNotification({
          type: 'error',
          message: t('GroupNotFound'),
          title: t.withBaseKey('CommonWords')('Error'),
        });
        return;
      }

      const existingInGroup = globalVariablesManager.getVariablesInGroup(groupName);
      if (existingInGroup.length > 0) {
        setPendingImportData(validation.data);
        setImportGroupName(groupName);
        setShowImportConfirm(true);
      } else {
        await handleGroupImportWithMode(groupName, 'replace', validation.data);
      }
    } catch (error) {
      console.error('Group import failed:', error);
      showNotification({
        type: 'error',
        message: t('ImportError'),
        title: t.withBaseKey('CommonWords')('Error'),
      });
    }
  }, [showNotification, t, handleGroupImportWithMode]);

  
  
  if (!canShowPanel) { return null; }

  // If minimized, don't render the full panel (it will be rendered by MinimizedPanelsContainer)
  if (isMinimized) {
    return null;
  }


  /**
   * Renders the appropriate control component based on control type
   * @param entry - The variable entry
   */
  const renderControl = (entry: GlobalVariable) => {
    const commonProps = {
      variable: {
        ...entry,
        // lastValue: localControlValues[entry.name]?.value ?? null,
      },
      name: entry.name,
      onChange: handleLocalControlValueChange,
    };

    switch (entry.controlType) {
      case 'number':
        return <NumberControl {...commonProps} />;
      case 'slider':
        return <SliderControl {...commonProps} />;
      case 'text':
        return <TextControl {...commonProps} />;
      case 'multilineText':
        return <MultilineTextControl {...commonProps} />;
      case 'dropdown':
        return <DropdownControl {...commonProps} />;
      case 'image':
        return <ImageControl {...commonProps} />;
      case 'binaryFile':
        return <BinaryFileControl {...commonProps} />;
      case 'checkbox':
        return <CheckboxControl {...commonProps} />;
      case 'multiSelect':
        return <MultiSelectControl {...commonProps} />;
      case 'anything':
        return <AnythingControl {...commonProps} />;
      case 'button':
        return <ButtonControl {...commonProps} onButtonClick={() => handleButtonClick(entry)} />;
      case 'group':
        return null; // Groups don't have controls
      default:
        return <TextControl {...commonProps} />;
    }
  };

  /**
   * Renders flattened items for drag and drop
   */
  const renderFlattenedItems = () => {
    return flattenedItems.map((item, index) => {
      const isGroup = globalVariablesManager.isGroupVariable(item.variable);
      const isExpanded = expandedGroups.has(item.variable.name);
      const hasChildren = item.children.length > 0;
      const displayDepth = item.id === activeId && projected ? projected.depth : item.depth;
      const isOver = overId === item.id && activeId !== item.id;
      
      // Calculate projected depth for this item when it's being dragged over
      const itemProjectedDepth = projected && overId === item.id ? projected.depth : undefined;
      
      // Determine if we're nesting (dropping into a group) vs reordering
      // Only indent indicator when nesting into a group
      // Nesting occurs when:
      // 1. The projected parentId matches the target group name (explicit nesting)
      // 2. The target is a group and projected depth is greater than target depth (dragging right)
      // 3. The target is a group and horizontal offset indicates nesting (dragging right)
      const isNesting = Boolean(isOver && projected && overId === item.id && isGroup &&
        (projected.parentId === item.variable.name || 
         projected.depth > item.depth ||
         offsetLeft > INDENTATION_WIDTH / 2));
      
      // Determine if indicator should be above or below based on drag position
      // When nesting into a group, always show BELOW the group (items go inside)
      // When reordering, show above when dragging up, below when dragging down
      const activeIndex = activeId ? flattenedItems.findIndex(({ id }) => id === activeId) : -1;
      const showIndicatorAbove = isOver && !isNesting && activeIndex !== -1 && activeIndex > index;
      
      return (
        <SortableVariableRow
          key={item.id}
          entry={item.variable}
          loading={dispatchingVariableChange[item.variable.name] ?? false}
          editingEntry={editingEntry}
          editingValue={editingValue}
          dispatchingVariableChange={dispatchingVariableChange}
          handleNameDoubleClick={handleNameDoubleClick}
          handleInputChange={handleInputChange}
          handleSaveEdit={handleSaveEdit}
          handleKeyPress={handleKeyPress}
          handleClearValue={handleClearValue}
          handleCopy={handleCopy}
          handleConfigureVariable={handleConfigureVariable}
          handleDeleteVariable={handleDeleteVariable}
          handlePushLocalValue={handlePushLocalValue}
          handleDownloadBinaryFile={handleDownloadBinaryFile}
          renderControl={renderControl}
          depth={displayDepth}
          isGroup={isGroup}
          isExpanded={isExpanded}
          hasChildren={hasChildren}
          onToggleGroup={isGroup ? () => toggleGroup(item.variable.name) : undefined}
          isOver={isOver}
          showIndicatorAbove={showIndicatorAbove}
          projectedDepth={itemProjectedDepth}
          isNesting={isNesting}
          onGroupExport={isGroup ? handleGroupExport : undefined}
          onGroupCopy={isGroup ? handleGroupCopy : undefined}
          onGroupPaste={isGroup ? handleGroupPaste : undefined}
          onGroupImport={isGroup ? handleGroupFileImport : undefined}
          canGroupPaste={canPaste}
        />
      );
    });
  };


  return (
    <>
      <Draggable
        onStop={handleStopDragging}
        bounds="parent"
        handle={`.${styles.Header}`}
        position={panelPosition}
        disabled={isMinimized}
      >
        <Resizable
          height={panelSize.height}
          width={panelSize.width}
          minConstraints={[minPanelSize.width, minPanelSize.height]}
          resizeHandles={['se']}
          onResize={handleResize}
        >
          <div
            className={styles.Container}
            style={{
              width: panelSize.width,
              height: panelSize.height,
              '--name-column-width': `${NAME_COLUMN_WIDTH_PERCENTAGE}%`,
              '--value-column-width': `${100 - NAME_COLUMN_WIDTH_PERCENTAGE}%`,
            } as React.CSSProperties}
          >
            <div className={styles.Header}>
              <span className={styles.DragIcon}>
                <HolderOutlined />
              </span>
              <span className={styles.Title}>{t('Title')}</span>
              <div style={{ display: 'flex', gap: '4px' }}>
                <ImportExportMenu
                  onDownload={handleExportClick}
                  onCopy={handleCopyClick}
                  onPaste={handlePasteClick}
                  onImport={handleImportClick}
                  canPaste={canPaste}
                  onOpenChange={handleMenuOpenChange}
                  saveValuesEnabled={userSettings.saveGlobalVariableValues}
                  onSaveValuesToggle={handleSaveValuesToggle}
                />
                <Tooltip title={t('AddVariable')} placement="top">
                  <Button
                    type="text"
                    icon={<PlusOutlined />}
                    onClick={() => setShowAddModal(true)}
                    className={styles.AddButton}
                  />
                </Tooltip>
                <PanelMinimizeButton onClick={handleMinimize} />
              </div>
            </div>
            
            <div className={styles.TableContainer}>
              <div className={styles.Table}>
                <div className={styles.TableHeader}>
                  <div className={`${styles.TableHeaderCell} ${styles.nameColumn}`}>{t('Name')}</div>
                  <div className={`${styles.TableHeaderCell} ${styles.valueColumn}`}>{t('Value')}</div>
                </div>
                <div className={styles.TableBody}>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={customCollisionDetection}
                    measuring={{
                      droppable: {
                        strategy: MeasuringStrategy.Always,
                      },
                    }}
                    onDragStart={handleDragStart}
                    onDragMove={handleDragMove}
                    onDragOver={handleDragOver}
                    onDragEnd={handleDragEnd}
                    onDragCancel={handleDragCancel}
                  >
                    <SortableContext items={sortedIds} strategy={verticalListSortingStrategy}>
                      {renderFlattenedItems()}
                      {createPortal(
                        <DragOverlay
                          dropAnimation={dropAnimationConfig}
                        >
                          {activeId && activeItem ? (
                            <SortableVariableRow
                              entry={activeItem.variable}
                              loading={false}
                              editingEntry={null}
                              editingValue=""
                              dispatchingVariableChange={{}}
                              handleNameDoubleClick={handleNameDoubleClick}
                              handleInputChange={handleInputChange}
                              handleSaveEdit={handleSaveEdit}
                              handleKeyPress={handleKeyPress}
                              handleClearValue={handleClearValue}
                              handleCopy={handleCopy}
                              handleConfigureVariable={handleConfigureVariable}
                              handleDeleteVariable={handleDeleteVariable}
                              handlePushLocalValue={handlePushLocalValue}
                              handleDownloadBinaryFile={handleDownloadBinaryFile}
                              renderControl={renderControl}
                              depth={activeItem.depth}
                              isGroup={globalVariablesManager.isGroupVariable(activeItem.variable)}
                              isExpanded={expandedGroups.has(activeItem.variable.name)}
                              hasChildren={activeItem.children.length > 0}
                              clone={true}
                              childCount={getChildCount(flattenedItems, activeId) + 1}
                            />
                          ) : null}
                        </DragOverlay>,
                        document.body
                      )}
                    </SortableContext>
                  </DndContext>
                </div>
              </div>
            </div>
          </div>
        </Resizable>
      </Draggable>

      <AddVariableModal
        visible={showAddModal}
        onClose={() => setShowAddModal(false)}
      />

      <ConfigurationModal
        visible={!!configuringVariable}
        variableName={configuringVariable}
        onClose={() => setConfiguringVariable(null)}
      />

      <ExportConfirmModal
        visible={showExportConfirm}
        onConfirm={handleExportConfirm}
        onCancel={() => {
          setShowExportConfirm(false);
          setExportAction(null);
          setExportGroupName(null);
        }}
      />

      <ImportConfirmModal
        visible={showImportConfirm}
        onConfirm={handleImportConfirm}
        onCancel={() => {
          setShowImportConfirm(false);
          setPendingImportData(null);
          setImportGroupName(null);
        }}
      />

      <input
        ref={fileInputRef}
        type="file"
        accept=".json"
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />
    </>
  );
};

export default GlobalVariablesPanel;
